import * as React from "react";
import { ChevronLeft, ChevronRight } from "./icons";

import { cn } from "utils/cn";
import { IconButton } from "./IconButton";

const numberFormat = new Intl.NumberFormat("en-IN");

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** With `pageSize`, shows "Showing 21–40 of 312" on the left. */
  totalItems?: number;
  pageSize?: number;
  /** Plural noun after the total, e.g. "invoices". */
  noun?: string;
  /** Blocks both buttons, e.g. while the next page is loading. */
  disabled?: boolean;
  className?: string;
}

/**
 * Previous / next pager for server-paged lists. Layout only: inside a DataTable pass it as
 * `footer`, which adds the divider and padding.
 */
export const Pagination = React.forwardRef<HTMLElement, PaginationProps>(
  (
    { page, totalPages, onPageChange, totalItems, pageSize, noun, disabled = false, className },
    ref
  ) => {
    const pageCount = Math.max(1, Math.ceil(totalPages) || 1);
    const current = Math.min(Math.max(1, page), pageCount);
    const hasTotals = totalItems !== undefined && pageSize !== undefined && pageSize > 0;

    let summary: string | null = null;
    if (hasTotals) {
      const total = Math.max(0, totalItems || 0);
      const suffix = noun ? ` ${noun}` : "";
      if (total === 0) {
        summary = `No ${noun || "results"}`;
      } else {
        const start = (current - 1) * (pageSize || 1) + 1;
        const end = Math.min(total, current * (pageSize || 1));
        summary = `Showing ${numberFormat.format(start)}–${numberFormat.format(
          end
        )} of ${numberFormat.format(total)}${suffix}`;
      }
    }

    return (
      <nav
        ref={ref}
        data-ui="pagination"
        aria-label="Pagination"
        className={cn(
          "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm text-muted-foreground",
          className
        )}
      >
        {summary && <p className="tabular-nums">{summary}</p>}
        <div className="ml-auto flex items-center gap-3">
          <span aria-live="polite" className="tabular-nums">
            Page <span className="font-medium text-foreground">{current}</span> of{" "}
            <span className="font-medium text-foreground">{pageCount}</span>
          </span>
          <div className="flex items-center gap-1.5">
            <IconButton
              label="Previous page"
              icon={<ChevronLeft aria-hidden="true" />}
              size="sm"
              variant="secondary"
              disabled={disabled || current <= 1}
              onClick={() => onPageChange(current - 1)}
            />
            <IconButton
              label="Next page"
              icon={<ChevronRight aria-hidden="true" />}
              size="sm"
              variant="secondary"
              disabled={disabled || current >= pageCount}
              onClick={() => onPageChange(current + 1)}
            />
          </div>
        </div>
      </nav>
    );
  }
);
Pagination.displayName = "Pagination";
